import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../services/axiosInstance';
import PageHeader from '../../components/common/PageHeader';
import { Sparkles, FileText, User, GraduationCap, Clock, AlertTriangle, CheckCircle2, ChevronRight, FileCheck } from 'lucide-react';
import { formatTimestamp } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import './styles.scss';

export default function AiInsights() {
  const { user } = useAuth();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchInsights();
  }, [user]);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/api/dashboard/ai-insights');
      if (res.data?.success) {
        setInsights(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch AI Insights:', err);
      setError('Failed to load AI Insights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDecisionIcon = (status) => {
    switch (status) {
      case 'APPROVED': return <CheckCircle2 size={16} className="text-success" />;
      case 'REJECTED': return <AlertTriangle size={16} className="text-danger" />;
      case 'NEEDS_REVIEW': return <Clock size={16} className="text-warning" />;
      default: return <FileText size={16} className="text-secondary" />;
    }
  };

  const getDecisionClass = (status) => {
    switch (status) {
      case 'APPROVED': return 'decision-approved';
      case 'REJECTED': return 'decision-rejected';
      case 'NEEDS_REVIEW': return 'decision-review';
      default: return 'decision-default';
    }
  };

  const formatDocType = (type) => {
    if (!type) return 'Unknown';
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  };

  return (
    <div className="ai-insights-page">
      <PageHeader 
        title={
          <span className="d-flex align-items-center gap-2">
            <Sparkles size={24} className="text-primary" />
            AI Insights Dashboard
          </span>
        }
        subtitle="Review recent AI-driven document verifications and automated decisions."
      />

      <div className="glass-panel table-container">
        {error && (
          <div className="alert alert-danger" style={{ margin: '0 1.5rem 1.5rem' }}>
            <AlertTriangle size={18} />
            {error}
          </div>
        )}

        {loading ? (
          <div className="table-loading">Loading AI Insights...</div>
        ) : (
          <table className="data-table insights-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Program</th>
                <th>Document Type</th>
                <th>AI Decision</th>
                <th>Confidence</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {insights.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-state">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '2rem 0' }}>
                      <FileCheck size={32} style={{ opacity: 0.5 }} />
                      <p style={{ margin: 0 }}>No AI insights found for your assigned programs.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                insights.map((insight) => (
                  <tr key={insight.documentId} className="clickable-row" onClick={() => navigate(`/applications/${insight.applicationId}`)}>
                    <td>
                      <span style={{ fontWeight: 500, color: '#1e293b' }}>
                         {insight.studentName}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                         {insight.programName}
                      </span>
                    </td>
                    <td>
                      <span className="doc-type-badge">
                        <FileText size={14} /> {formatDocType(insight.documentType)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-1">
                        <span className={`decision-badge ${getDecisionClass(insight.decisionStatus)}`}>
                          {getDecisionIcon(insight.decisionStatus)}
                          {insight.decisionStatus?.replace('_', ' ')}
                        </span>
                        {insight.decisionDescription && (
                          <span style={{ fontSize: '0.8rem', color: '#475569', maxWidth: '350px', display: 'block', lineHeight: '1.4', marginTop: '4px' }} title={insight.decisionDescription}>
                            {insight.decisionDescription}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {insight.confidenceScore !== null && insight.confidenceScore !== undefined ? (
                        <div 
                          className="confidence-indicator" 
                          title="AI's assessment of how likely this document is valid and meets all requirements."
                        >
                          <div className="progress" style={{ height: '6px', width: '60px', backgroundColor: '#e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
                            <div 
                              className={`progress-bar`}
                              style={{ width: `${insight.confidenceScore * 100}%`, backgroundColor: insight.confidenceScore >= 0.8 ? '#10b981' : insight.confidenceScore >= 0.5 ? '#f59e0b' : '#ef4444', height: '100%' }}
                            />
                          </div>
                          <span style={{ fontSize: '0.8rem', color: '#64748b', marginLeft: '8px' }}>
                            {Math.round(insight.confidenceScore * 100)}%
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>N/A</span>
                      )}
                    </td>
                    <td>
                      <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                        {insight.insightDate ? formatTimestamp(insight.insightDate, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                      </span>
                    </td>
                    <td>
                      <button 
                        className="btn-icon"
                        title="View Application Details"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/applications/${insight.applicationId}`)
                        }}
                      >
                        <ChevronRight size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
